// src/components/home/StatisticsSection.js
import React, { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { animateCounter } from '../../utils/animations';
import Section from '../common/Section';
import Heading from '../common/Heading';
import Button from '../common/Button';

const StatisticsSection = () => {
  // Create refs for statistics counters
  const trainedRef = useRef(null);
  const cohortsRef = useRef(null);
  const eventsRef = useRef(null);
  const communityRef = useRef(null);
  
  // Setup intersection observer
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  
  // Animate counters when section comes into view
  useEffect(() => {
    if (inView) {
      animateCounter(trainedRef.current, 1000, 2, 0.1);
      animateCounter(cohortsRef.current, 13, 2, 0.3);
      animateCounter(eventsRef.current, 20, 2, 0.5);
      animateCounter(communityRef.current, 3000, 2, 0.7);
    }
  }, [inView]);
  
  const stats = [
    {
      ref: trainedRef,
      value: '1000+',
      label: 'Youths Trained',
      description: 'Successful graduates with marketable digital skills'
    },
    {
      ref: cohortsRef,
      value: '13',
      label: 'Successful Cohorts',
      description: '3-month intensive training cycles completed'
    },
    {
      ref: eventsRef,
      value: '20+',
      label: 'High Impact Events',
      description: 'Career talks, workshops, and field trips'
    },
    {
      ref: communityRef,
      value: '3,000+',
      label: 'Youth Community',
      description: 'Connected through social media and WhatsApp'
    }
  ];
  
  return (
    <Section background="white" className="overflow-hidden">
      <div ref={sectionRef} className="py-10">
        <Heading level={2} align="center" className="mb-16" withAccent>
          Our Impact in Numbers
        </Heading>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 mb-14">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="reveal reveal-delay-1 bg-white rounded-lg shadow-lg p-8 text-center border-b-4 border-primary transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
            >
              <div className="text-4xl md:text-5xl font-bold text-primary-dark mb-4">
                <span ref={stat.ref}>0</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{stat.label}</h3>
              <p className="text-gray-600">{stat.description}</p>
            </div>
          ))}
        </div>
        
        {/* WhatsApp Community Button */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <a 
            href="https://chat.whatsapp.com/JktLtRuNOIlAg7mIRACuM2"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-green-500 text-white font-medium text-lg rounded-md hover:bg-green-600 transition-colors shadow-lg"
          >
            <FaWhatsapp className="mr-3 text-2xl" />
            Join Our WhatsApp Community
          </a>
          <p className="text-gray-600 mt-3">
            Connect with other youths, share ideas, and stay updated on our latest programs
          </p>
        </motion.div>
      </div>
    </Section>
  );
};

export default StatisticsSection;